import { updateProject } from "@/components/common/project-provider/update-project";
import { ElementIconType } from "@/components/ui/icons/element-icon-type";
import { OnlyIElementsStore } from "@/store/ielements-slice-selector";
import { changeMode } from "@/store/mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { assert } from "@faro-lotv/foundation";
import {
  IElementAreaSection,
  IElementGenericImgSheet,
  isIElementAreaSection,
  isIElementGenericImgSheet,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import {
  addToLoadingRoots,
  removeFromLoadingRoots,
  selectChildrenDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import {
  createMutationDuplicateArea,
  ProjectApi,
} from "@faro-lotv/service-wires";
import {
  ContextMenuAction,
  ContextMenuActionHandlerArgs,
  ContextMenuActionType,
} from "../../action-types";

/**
 * @returns The area's floorplan sheets, excluding capture overview maps.
 * @param area The area to duplicate.
 * @param state The loaded project state.
 */
export function getSheetsToDuplicate(
  area: IElementAreaSection,
  state: OnlyIElementsStore,
): IElementGenericImgSheet[] {
  return selectChildrenDepthFirst(
    area,
    isIElementGenericImgSheet,
    2,
  )(state).filter((sheet) => sheet.typeHint === IElementTypeHint.area);
}

function createDuplicateSheetAction(
  includeMarkers: boolean,
): ContextMenuAction {
  return {
    type: includeMarkers
      ? ContextMenuActionType.duplicateSheetWithMarkers
      : ContextMenuActionType.duplicateSheet,
    label: includeMarkers ? "Sheets and location markers" : "Sheets only",
    icon: ElementIconType.CopyIcon,
    handler: (args) =>
      duplicateSheet(args, args.apiClients.projectApiClient, includeMarkers),
  };
}

/**
 * @param args The selected element, state, and UI callbacks.
 * @param projectApi The current project's API client.
 * @param includeMarkers Whether to copy empty location markers.
 */
export async function duplicateSheet(
  {
    elementID,
    state,
    dispatch,
    openToast,
  }: Pick<
    ContextMenuActionHandlerArgs,
    "elementID" | "state" | "dispatch" | "openToast"
  >,
  projectApi: ProjectApi,
  includeMarkers: boolean,
): Promise<void> {
  assert(
    selectHasWritePermission(state),
    "Write permission is required to duplicate sheets",
  );
  const area = selectIElement(elementID)(state);
  assert(
    area && isIElementAreaSection(area) && area.parentId,
    "The selected element must be an area",
  );
  const sheets = getSheetsToDuplicate(area, state);
  assert(sheets.length > 0, "No floorplan sheets are available to duplicate");
  const mutation = createMutationDuplicateArea({
    areaId: area.id,
    includeLocationMarkers: includeMarkers,
  });
  const areaId = mutation.newAreaId;
  dispatch(addToLoadingRoots(area.id));
  try {
    const results = await projectApi.applyMutations([mutation]);
    const failure = results.find((result) => result.status === "failure");
    assert(!failure, failure?.message ?? "Unable to duplicate the sheet");
    // Query filters are AND-combined, so fetch the new subtree and its parent link separately.
    await dispatch(
      updateProject({ projectApi, iElementQuery: { ancestorIds: [areaId] } }),
    );
    await dispatch(
      updateProject({
        projectApi,
        iElementQuery: { ids: [area.parentId, areaId] },
      }),
    );
    dispatch(setActiveElement(areaId));
    // The copy has no captures, so 3D modes would have nothing to show.
    dispatch(changeMode("sheet"));
    openToast({
      title: sheets.length === 1 ? "Sheet duplicated" : "Sheets duplicated",
    });
  } finally {
    dispatch(removeFromLoadingRoots(area.id));
  }
}

export const DUPLICATE_SHEET_ACTION = createDuplicateSheetAction(false);
export const DUPLICATE_SHEET_WITH_MARKERS_ACTION =
  createDuplicateSheetAction(true);
